import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  Field,
  FieldDescription,
  FieldLabel,
  InputGroup,
  InputGroupAddon,
  InputGroupTextarea,
} from "@beweegbaas/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Outlet, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useEffect, useState } from "react";
import { Page } from "#/components/page";
import {
  getTeacherLesNotities,
  updateNotitie,
} from "#/modules/les.docent-gestuurd/lesson-teacher.functions";
import { useDebouncedCallback } from "#/modules/les.docent-gestuurd/use-debounced-callback";
import { useDocentLes } from "#/modules/les.docent-gestuurd/use-docent-les";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lesson/$lesson/teacher/notities",
)({
  loader: ({ params }) => getTeacherLesNotities({ data: { lesId: params.lesson } }),
  component: TeacherLessonNotities,
});

const SAVE_DEBOUNCE_MS = 500;
const SUCCESS_SHOW_MS = 2000;

function TeacherLessonNotities() {
  const { notities } = Route.useLoaderData();
  const { locale, lesson: lessonId } = Route.useParams();
  const { lesson } = useDocentLes();
  const router = useRouter();
  const updateNotitieFn = useServerFn(updateNotitie);

  const lesNotitie = notities?.lesNotitie;
  const relatedNotities = notities?.relatedNotities ?? [];
  const [notitie, setNotitie] = useState(lesNotitie?.notitie ?? "");
  const [showSuccess, setShowSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: (value: string) => updateNotitieFn({ data: { lesId: lessonId, notitie: value } }),
    onSuccess: () => {
      void router.invalidate();
      setShowSuccess(true);
    },
  });

  useEffect(() => {
    if (!showSuccess) return;
    const t = setTimeout(() => setShowSuccess(false), SUCCESS_SHOW_MS);
    return () => clearTimeout(t);
  }, [showSuccess]);

  const debouncedSave = useDebouncedCallback((value: string) => {
    mutation.mutate(value);
  }, SAVE_DEBOUNCE_MS);

  return (
    <Page.Container className="flex flex-col gap-6">
      <div className="flex max-w-[75ch] flex-col gap-3">
        <Field>
          <FieldLabel htmlFor="lesson-notitie">{m["common.notitie"]()}</FieldLabel>

          <InputGroup>
            <InputGroupTextarea
              className="min-h-auto"
              id="lesson-notitie"
              rows={4}
              placeholder={m["les.notities.placeholder"]()}
              value={notitie}
              onChange={(e) => {
                const value = e.target.value;
                setNotitie(value);
                debouncedSave(value);
              }}
            />
            <InputGroupAddon align="block-end">
              <span
                tabIndex={-1}
                aria-label={m["les.administratie.add_remark"]()}
                className={cn(
                  `bg-muted squircle pointer-events-none ml-auto flex
                      size-8 items-center justify-center rounded
                      supports-squircle:rounded-full`,
                  {
                    "bg-success text-white opacity-100": notitie,
                  },
                )}
              >
                <IconIconamoonCommentAddFill
                  className={cn("size-4 opacity-20", {
                    "opacity-100": notitie,
                  })}
                />
              </span>
            </InputGroupAddon>
          </InputGroup>

          <FieldDescription>{m["les.notities.description"]()}</FieldDescription>
        </Field>

        {relatedNotities.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="">
                {m["les.notities.related_notes"]({
                  klasNaam: lesson.klasName,
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul
                className="m-0 flex list-none flex-col divide-y divide-black/10
                p-0"
              >
                {relatedNotities.map((note) => (
                  <li
                    key={note.id}
                    className="py-3 not-first:pt-3 not-last:pb-3 first:pt-0
                    last:pb-0"
                  >
                    <p
                      className="m-0 max-w-[75ch] text-sm text-pretty
                      whitespace-pre-wrap"
                    >
                      {note.notitie}
                    </p>
                    <p className="text-muted-foreground m-0 mt-1 text-xs">
                      {note.docentNaam} ·{" "}
                      {new Date(note.updatedAt).toLocaleDateString(locale, {
                        dateStyle: "medium",
                      })}
                    </p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
      <Outlet />
    </Page.Container>
  );
}
